import { EAV_ATTRIBUTE_TYPES_EXCLUDED_IN_PERSONAL_ACCOUNT } from '@zaoblako/shared/modules/eav/constants/eav-attribute-types-excluded-in-personal-account.constants';
import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { EavAttributeValueTypeEnumI18n } from '@zaoblako/shared/modules/eav/i18n/eav-attribute-value-type-enum.i18n';
import { resolveEavAttributeShowInPersonalAccount } from '@zaoblako/shared/modules/eav/utils/resolve-eav-attribute-show-in-personal-account.util';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { EavDtoKeysEnum } from '@zaoblako/shared/modules/eav/enums/eav-dto-keys.enum';
import { idLikeSchema } from '@zaoblako/shared/modules/serde/schemas/id-like.schema';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { mathJsExpressionSchema } from '@zaoblako/shared/modules/serde/schemas/math-js-expression.schema';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import type { HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';

import * as yup from 'yup';

/** EAV attribute created and update APIs request body - DTO signature */
export interface EavAttributeFormInterface {
  /** Unique ID. If defined and attribute exists, then it will be updated. */
  id?: number;
  /** EAV attribute display name */
  name: string;
  /** EAV attribute value type */
  type: EavAttributeValueTypeEnum;
  /**
   * If `true` then several value will be array of selected objects.
   * Using only when {@link EavAttributeEntity.type} equals:
   *   - {@link EavAttributeValueTypeEnum.FILIAL} (multiple filials selection);
   *   - {@link EavAttributeValueTypeEnum.COST_CENTER} (multiple cost centers selection);
   */
  multipleSelect?: boolean;
  /** Expression to compute. Using only when {@link EavAttributeFindInterface.type} equals {@link EavAttributeValueTypeEnum.COMPUTED}. */
  expression?: string;
  /**
   * HelpDesk task statuses where this attribute's value is required.
   * Using only for EAV entities related to HD tasks.
   */
  requiredOnHdTaskStatuses?: HelpdeskTaskStatusFindInterface[];
  /** If `true`, attribute is visible in client personal account HelpDesk */
  showInPersonalAccount?: boolean;
}

/** EAV attribute created and update APIs request body - DTO schema */
export const EavAttributeFormDto = SerdeDtoClass.create<EavAttributeFormInterface>(EavDtoKeysEnum.EAV_ATTRIBUTE_FORM_DTO, {
  id: {
    optional: true,
    label: 'Номер',
    serializer: idLikeSchema,
  },
  name: {
    label: 'Название',
    serializer: nonEmptyStringSchema,
  },
  type: {
    label: 'Тип',
    serializer: commonEnumValidator(EavAttributeValueTypeEnum, EavAttributeValueTypeEnumI18n),
  },
  multipleSelect: {
    optional: true,
    label: 'Множественный выбор',
    serializer: booleanSchema,
  },
  expression: {
    optional: context => (context?.value as EavAttributeFormInterface)?.type !== EavAttributeValueTypeEnum.COMPUTED,
    label: 'Выражение для вычисляемого поля',
    serializer: mathJsExpressionSchema,
  },
  requiredOnHdTaskStatuses: {
    optional: true,
    label: 'Обязателен для заявок HD со статусами',
    serializer: yup.array(entityLikeSchema),
  },
  showInPersonalAccount: {
    optional: true,
    label: 'Отражать в ЛК Клиента',
    serializer: () => booleanSchema
      .transform((value, context) => {
        const attribute = context?.parent as EavAttributeFormInterface | undefined;
        if (!attribute?.type) {
          return value === true;
        }

        return resolveEavAttributeShowInPersonalAccount(attribute.type, value);
      })
      .test({
        message: 'Нельзя отражать в ЛК поля типа «Центр затрат», «Филиал», «Помещение» или «Вычисляемое поле»',
        test(value, context) {
          const attribute = context?.parent as EavAttributeFormInterface | undefined;
          if (!attribute?.type || value !== true) {
            return true;
          }

          return !EAV_ATTRIBUTE_TYPES_EXCLUDED_IN_PERSONAL_ACCOUNT.includes(attribute.type);
        },
      }),
  },
});
